import { useState } from "react";
import { Box, Button, CircularProgress, Link, MenuItem, Typography } from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  PhoneAndroid as PhoneIcon,
  Cake as CakeIcon,
  Wc as GenderIcon,
  Lock as LockIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { postData } from "../../Webservice/Webservice";
import { goldButtonSx, fadeUp, slideIn } from "./signinStyles";
import { Field, PasswordField, OtpBox, rules } from "./SignupParts";
import { SwapTabs, SwapPanes } from "./SwapUI";

const iconSx = { color: "#C8A054", fontSize: 20 };
const today = () => new Date().toISOString().split("T")[0];
const twoCols = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 1.5 };

// Customer sign up. Pick ONE way to register, shown as two separate sections:
//   Mobile number            -> confirmed with an SMS code
//   Email & password         -> confirmed with an email code
// Name, date of birth and gender are optional.
function CustomerSignupForm({ onSignedUp, onError }) {
  const [step, setStep] = useState("details"); // "details" | "verify" | "done"
  const [method, setMethod] = useState("mobile"); // "mobile" | "email"
  const [form, setForm] = useState({ name: "", email: "", mobile: "", dob: "", gender: "", password: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState({ password: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState({ email: false, mobile: false });

  const set = (name) => (e) => {
    let value = e.target.value;
    if (name === "mobile") value = value.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, [name]: value }));
    onError("");
    if (errors[name]) setErrors((er) => { const n = { ...er }; delete n[name]; return n; });
  };

  const changeMethod = (m) => {
    setMethod(m);
    setErrors({});
    onError("");
  };

  // only the chosen way is required; the optional details just have to look right when filled
  const validate = () => {
    const e = {};
    if (method === "mobile") {
      if (!form.mobile) e.mobile = "Enter your mobile number";
      else if (!rules.mobile.test(form.mobile)) e.mobile = "Enter a valid 10-digit mobile number";
    } else {
      if (!form.email.trim()) e.email = "Enter your email address";
      else if (!rules.email.test(form.email.trim())) e.email = "Enter a valid email address";
      if (!form.password) e.password = "Enter a password";
      else if (form.password.length < 8) e.password = "At least 8 characters";
      if (form.confirmPassword && form.confirmPassword !== form.password) e.confirmPassword = "Passwords do not match";
    }
    if (form.name.trim() && form.name.trim().length < 2) e.name = "Enter your full name";
    if (form.dob && form.dob > today()) e.dob = "Date of birth cannot be in the future";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const start = async (ev) => {
    ev.preventDefault();
    onError("");
    if (!validate()) return;
    setLoading(true);
    try {
      await postData("customer/web/signup/start", {
        ...(form.name.trim() && { name: form.name.trim() }),
        ...(form.dob && { dob: form.dob }),
        ...(form.gender && { gender: form.gender }),
        ...(method === "mobile"
          ? { mobile: form.mobile }
          : { email: form.email.trim(), password: form.password }),
      }, "", false, false);
      setDone({ email: false, mobile: false });
      setStep("verify");
    } catch (err) {
      onError(err.message || "Could not start sign up. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const afterVerify = (res) => {
    setDone({ email: !!res.emailVerified, mobile: !!res.mobileVerified });
    if (res.done && res.token) {
      setStep("done");
      setTimeout(() => onSignedUp(res), 1400);
    }
  };

  const verifyEmail = async (code) => afterVerify(await postData("customer/web/signup/verify-email", { email: form.email.trim(), code }, "", false, false));
  const verifyMobile = async (code) => afterVerify(await postData("customer/web/signup/verify-mobile", { mobile: form.mobile, code }, "", false, false));
  const resend = (channel) => () => postData("customer/web/signup/resend", { channel, email: form.email.trim(), mobile: form.mobile }, "", false, false);

  if (step === "done") {
    return (
      <Box sx={{ textAlign: "center", py: 3, animation: `${fadeUp} 0.5s both` }}>
        <CheckIcon sx={{ fontSize: 64, color: "#2e7d32" }} />
        <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>You&apos;re all set</Typography>
        <Typography variant="body2" color="text.secondary">Your account is ready. Taking you to the shop…</Typography>
      </Box>
    );
  }

  if (step === "verify") {
    return (
      <Box sx={{ animation: `${slideIn} 0.5s both` }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: "center" }}>
          Enter the code to finish. It is valid for 5 minutes.
        </Typography>
        {method === "email" ? (
          <OtpBox
            icon={<EmailIcon sx={{ fontSize: 20 }} />}
            title="Email"
            target={form.email.trim()}
            verified={done.email}
            onVerify={verifyEmail}
            onResend={resend("email")}
          />
        ) : (
          <OtpBox
            icon={<PhoneIcon sx={{ fontSize: 20 }} />}
            title="Mobile number"
            target={`+91 ${form.mobile}`}
            verified={done.mobile}
            onVerify={verifyMobile}
            onResend={resend("mobile")}
          />
        )}
        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
          <Link component="button" type="button" underline="hover" onClick={() => { onError(""); setStep("details"); }} sx={{ color: "#8a6a24", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <ArrowBackIcon sx={{ fontSize: 17 }} />
            Edit my details
          </Link>
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate onSubmit={start} sx={{ animation: `${fadeUp} 0.5s both` }}>
      {/* two separate ways to register */}
      <SwapTabs
        value={method}
        onChange={changeMethod}
        options={[
          { value: "mobile", label: "Mobile number", icon: <PhoneIcon sx={{ fontSize: 18 }} /> },
          { value: "email", label: "Email & password", icon: <EmailIcon sx={{ fontSize: 18 }} /> },
        ]}
      />

      <SwapPanes index={method === "mobile" ? 0 : 1}>
        {/* way 1: mobile number */}
        <Field
          name="mobile"
          label="Mobile number"
          value={form.mobile}
          onChange={set("mobile")}
          error={errors.mobile}
          icon={<><PhoneIcon sx={{ ...iconSx, mr: 0.75 }} />+91</>}
          helper="We will send a code by SMS"
          inputProps={{ inputMode: "numeric", maxLength: 10 }}
        />

        {/* way 2: email and password */}
        <Box>
          <Field name="email" label="Email address" type="email" value={form.email} onChange={set("email")} error={errors.email} icon={<EmailIcon sx={iconSx} />} />
          <Box sx={twoCols}>
            <PasswordField name="password" label="Password" value={form.password} onChange={set("password")} error={errors.password} icon={<LockIcon sx={iconSx} />} show={show.password} onToggle={() => setShow((s) => ({ ...s, password: !s.password }))} />
            <PasswordField name="confirmPassword" label="Confirm password" value={form.confirmPassword} onChange={set("confirmPassword")} error={errors.confirmPassword} icon={<LockIcon sx={iconSx} />} show={show.confirm} onToggle={() => setShow((s) => ({ ...s, confirm: !s.confirm }))} />
          </Box>
        </Box>
      </SwapPanes>

      {/* optional details */}
      <Field name="name" label="Full name" value={form.name} onChange={set("name")} error={errors.name} icon={<PersonIcon sx={iconSx} />} />
      <Box sx={twoCols}>
        <Field name="dob" label="Date of birth" type="date" value={form.dob} onChange={set("dob")} error={errors.dob} icon={<CakeIcon sx={iconSx} />} inputProps={{ max: today() }} />
        <Field name="gender" label="Gender" select value={form.gender} onChange={set("gender")} error={errors.gender} icon={<GenderIcon sx={iconSx} />}>
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </Field>
      </Box>

      <Button type="submit" fullWidth variant="contained" disabled={loading} startIcon={loading ? null : <PersonAddIcon />} sx={{ ...goldButtonSx, mt: 0.5 }}>
        {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Create account"}
      </Button>
    </Box>
  );
}

export default CustomerSignupForm;
